import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Car, Calendar, History, LogOut, Plus, Clock, MapPin, 
  ChevronRight, RefreshCw, AlertCircle, CheckCircle2, Loader2,
  CalendarDays, CreditCard, Star, Droplets, Sparkles, AlertTriangle,
  HelpCircle
} from 'lucide-react';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';
import PendingReviewBanner from '@/components/PendingReviewBanner';
import ReviewModal from '@/components/ReviewModal';
import OnboardingTour from '@/components/OnboardingTour';
import ReminderBanner from '@/components/ReminderBanner';
import AppointmentCountdown from '@/components/AppointmentCountdown';
import SupportModal from '@/components/SupportModal';

// Mapeamento de tipo de serviço
const SERVICE_TYPE_LABELS = {
  complete: 'Lavagem Completa',
  lavagem_completa: 'Lavagem Completa',
  ducha: 'Ducha Simples',
  ducha_simples: 'Ducha Simples',
};

// Status badges com cores - Design System
const STATUS_CONFIG = {
  PENDING: { label: 'Aguardando', color: 'bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20', icon: Clock },
  ACCEPTED: { label: 'Confirmado', color: 'bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20', icon: CheckCircle2 },
  IN_PROGRESS: { label: 'Em execução', color: 'bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20', icon: Car },
  AWAITING_CODE: { label: 'Aguardando código', color: 'bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20', icon: AlertCircle },
  PAID: { label: 'Finalizado', color: 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20', icon: CheckCircle2 },
  COMPLETED: { label: 'Concluído', color: 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20', icon: CheckCircle2 },
  CANCELLED: { label: 'Cancelado', color: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20', icon: AlertCircle },
};

// Status do plano (resumo compacto da Home) - Design System
const PLAN_STATUS_CONFIG = {
  active: { label: 'Ativo', color: 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20' },
  pending: { label: 'Ativando...', color: 'bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20', spinner: true },
  processing: { label: 'Ativando...', color: 'bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20', spinner: true },
  past_due: { label: 'Pagamento não aprovado', color: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20' },
  cancelled: { label: 'Cancelado', color: 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20' },
  expired: { label: 'Expirado', color: 'bg-slate-100 text-slate-600 border border-slate-200' },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Estado para avaliações pendentes
  const [pendingReviews, setPendingReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  
  // Estado para contestações
  const [disputedAppointments, setDisputedAppointments] = useState([]);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [disputeAppointment, setDisputeAppointment] = useState(null);
  const [disputeMotivo, setDisputeMotivo] = useState('');
  const [disputeDescricao, setDisputeDescricao] = useState('');
  const [disputeLoading, setDisputeLoading] = useState(false);
  
  // Estado para suporte
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    
    // Verificar onboarding antes de carregar dados
    checkOnboardingAndFetch();
  }, [user, navigate]);

  const checkOnboardingAndFetch = async () => {
    try {
      const token = localStorage.getItem('token');
      const onboardingRes = await axios.get(`${API}/client/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Se onboarding não está completo, redirecionar
      if (!onboardingRes.data.onboarding_complete) {
        if (onboardingRes.data.next_step === 'vehicle') {
          navigate('/client/onboarding/vehicle', { replace: true });
        } else if (onboardingRes.data.next_step === 'plan') {
          navigate('/client/onboarding/plan', { replace: true });
        }
        return;
      }
      
      // Onboarding completo, carregar dados
      fetchData();
    } catch (error) {
      console.error('Erro ao verificar onboarding:', error);
      fetchData();
    }
  };

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Buscar dados em paralelo (incluindo avaliações pendentes e disputas)
      const [appointmentsRes, subscriptionRes, pendingReviewsRes, disputesRes] = await Promise.all([
        axios.get(`${API}/appointments/client`, { headers }).catch(() => ({ data: { appointments: [] } })),
        axios.get(`${API}/client/subscription-details`, { headers }).catch(() => ({ data: { subscription: null } })),
        axios.get(`${API}/reviews/pending`, { headers }).catch(() => ({ data: { pending_reviews: [] } })),
        axios.get(`${API}/disputes/my`, { headers }).catch(() => ({ data: { disputes: [] } }))
      ]);
      
      setAppointments(appointmentsRes.data.appointments || []);
      setSubscription(subscriptionRes.data.subscription);
      setPendingReviews(pendingReviewsRes.data.pending_reviews || []);
      
      // Extrair IDs de appointments já contestados
      const disputedIds = (disputesRes.data.disputes || []).map(d => d.appointment_id);
      setDisputedAppointments(disputedIds);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handler para abrir modal de avaliação
  const handleOpenReview = (review) => {
    setSelectedReview(review);
    setShowReviewModal(true);
  };

  // Handler após enviar avaliação
  const handleReviewSubmitted = () => {
    setShowReviewModal(false);
    setSelectedReview(null);
    fetchData(true); // Recarregar dados silenciosamente
  };

  // Abrir modal de contestação
  const handleOpenDispute = (appointment) => {
    setDisputeAppointment(appointment);
    setDisputeMotivo('');
    setDisputeDescricao('');
    setDisputeModalOpen(true);
  };

  // Enviar contestação
  const handleSubmitDispute = async () => {
    if (!disputeMotivo) {
      toast.error('Selecione o motivo da contestação');
      return;
    }
    if (!disputeDescricao || disputeDescricao.length < 20) {
      toast.error('Descreva o problema com pelo menos 20 caracteres');
      return;
    }

    setDisputeLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/disputes`, {
        appointment_id: disputeAppointment.id,
        motivo: disputeMotivo,
        descricao: disputeDescricao
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Contestação enviada para análise!');
      setDisputeModalOpen(false);
      // PROMPT BLINDADO: Refetch completo após criar contestação
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar contestação');
    } finally {
      setDisputeLoading(false);
    }
  };

  // Verificar se pode contestar
  const canDispute = (appointment) => {
    const isCompleted = ['COMPLETED', 'PAID'].includes(appointment.status);
    const notDisputed = !disputedAppointments.includes(appointment.id);
    return isCompleted && notDisputed;
  };

  const handleEmailVerified = () => {
    if (refreshUser) refreshUser();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
  };

  // Separar agendamentos ativos e histórico
  const activeAppointments = appointments.filter(a => 
    ['PENDING', 'ACCEPTED', 'IN_PROGRESS', 'AWAITING_CODE'].includes(a.status)
  );
  const recentCompleted = appointments
    .filter(a => ['PAID', 'COMPLETED', 'CANCELLED'].includes(a.status))
    .slice(0, 3);

  // Calcular uso do plano (usando formato do subscription-details)

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* ==================== ONBOARDING TOUR (OVERLAY) ==================== */}
      <OnboardingTour userType="client" />
      
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-full">
              <Car className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold" data-testid="greeting">
                Olá, {user?.name?.split(' ')[0]}! 👋
              </h1>
              <p className="text-xs text-muted-foreground">Bem-vindo à JATOO</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSupportModalOpen(true)}
              title="Precisa de ajuda?"
              data-testid="support-btn"
            >
              <HelpCircle className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { logout(); navigate('/'); }} 
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Banner de verificação de e-mail */}
        <EmailVerificationBanner user={user} onVerified={handleEmailVerified} />

        {/* Banner de lembretes de agendamento */}
        <ReminderBanner />

        {/* Banner de avaliação pendente */}
        {pendingReviews.length > 0 && (
          <PendingReviewBanner 
            pendingReview={pendingReviews[0]} 
            onReviewClick={() => handleOpenReview(pendingReviews[0])} 
          />
        )}

        {/* Botão principal - Agendar */}
        <Card 
          className="p-6 bg-gradient-to-r from-slate-900 to-slate-800 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/client/schedule')}
          data-testid="schedule-main-btn"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/10 p-3 rounded-xl">
                <Plus className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Agendar Lavagem</h2>
                <p className="text-white/70 text-sm">Encontre o lava-jato mais próximo</p>
              </div>
            </div>
            <ChevronRight className="w-6 h-6" />
          </div>
        </Card>

        {/* Pass 10C-II: resumo compacto "Meu Plano" (somente leitura, sem "Ver detalhes"). */}
        <Card className="p-4" data-testid="home-plan-summary">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Meu Plano</p>
            {subscription && (
              <Badge className={`inline-flex items-center gap-1 ${PLAN_STATUS_CONFIG[subscription.status]?.color || 'bg-slate-100 text-slate-600'}`} data-testid="home-plan-status">
                {PLAN_STATUS_CONFIG[subscription.status]?.spinner && <Loader2 className="w-3 h-3 animate-spin" />}
                {PLAN_STATUS_CONFIG[subscription.status]?.label || subscription.status}
              </Badge>
            )}
          </div>
          {subscription ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-lg shrink-0">
                  <CreditCard className="w-5 h-5 text-primary" />
                </div>
                <p className="font-semibold text-base truncate" data-testid="home-plan-name">
                  {subscription.plan?.name || 'Plano Ativo'}
                </p>
              </div>
              {/* Datas lado a lado */}
              <div className="flex items-start justify-between gap-3 pt-1">
                {subscription.start_date && (
                  <div data-testid="home-plan-start">
                    <p className="text-xs text-muted-foreground">Data de início</p>
                    <p className="text-sm font-medium">
                      {new Date(subscription.start_date).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                )}
                {subscription.next_billing_date && (
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Próxima renovação</p>
                    <p className="text-sm font-medium" data-testid="home-plan-next-billing">
                      {new Date(subscription.next_billing_date).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                )}
              </div>

              {/* Valor mensal em linha própria, abaixo das datas */}
              <div data-testid="home-plan-price-block">
                <p className="text-xs text-muted-foreground">Valor mensal</p>
                <p className="text-lg font-bold text-primary" data-testid="home-plan-price">
                  {formatCurrency(subscription.plan?.price || 0)}
                </p>
              </div>

              {(subscription.usage?.complete?.total > 0 || subscription.usage?.ducha?.total > 0) && (
                <div className="pt-3 border-t" data-testid="home-plan-services">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Serviços incluídos</p>
                  <div className="space-y-3">
                    {subscription.usage?.complete?.total > 0 && (
                      <div className="flex items-center gap-3" data-testid="home-service-complete">
                        <div className="bg-blue-100 p-2 rounded-lg shrink-0">
                          <Sparkles className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-base font-medium">Lavagem Completa</p>
                          <p className="text-sm text-muted-foreground">
                            {subscription.usage.complete.used} usadas · {Math.max(0, subscription.usage.complete.total - subscription.usage.complete.used)} restantes
                          </p>
                        </div>
                      </div>
                    )}
                    {subscription.usage?.ducha?.total > 0 && (
                      <div className="flex items-center gap-3" data-testid="home-service-ducha">
                        <div className="bg-cyan-100 p-2 rounded-lg shrink-0">
                          <Droplets className="w-4 h-4 text-cyan-600" />
                        </div>
                        <div>
                          <p className="text-base font-medium">Ducha Simples</p>
                          <p className="text-sm text-muted-foreground">
                            {subscription.usage.ducha.used} usadas · {Math.max(0, subscription.usage.ducha.total - subscription.usage.ducha.used)} restantes
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3" data-testid="home-plan-empty">
              <div className="bg-slate-100 p-2 rounded-lg shrink-0">
                <CreditCard className="w-5 h-5 text-slate-500" />
              </div>
              <p className="text-sm text-muted-foreground">Você ainda não possui um plano ativo.</p>
            </div>
          )}
        </Card>

        {/* Ações Rápidas */}
        <div className="grid grid-cols-1 gap-3">
          <Card 
            className="p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/client/support')}
            data-testid="support-page-btn"
          >
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <HelpCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium">Suporte</p>
                <p className="text-xs text-muted-foreground">Fale conosco</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Pass 10B-A: "Seus Agendamentos" e "Histórico Recente" movidos para a aba Agendamentos (/client/appointments). Ocultos na Home. */}
        {false && (<>
        {/* Agendamentos Ativos */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" />
              Seus Agendamentos
            </h2>
          </div>

          {activeAppointments.length === 0 ? (
            <Card className="p-6 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Nenhum agendamento ativo</p>
              <Button 
                variant="link" 
                className="mt-2"
                onClick={() => navigate('/client/schedule')}
              >
                Agendar agora
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {activeAppointments.slice(0, 3).map(appointment => {
                const statusConfig = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.PENDING;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <Card 
                    key={appointment.id} 
                    className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate('/client/appointments')}
                    data-testid={`appointment-card-${appointment.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            {formatDate(appointment.date)}
                          </span>
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            {appointment.time}
                          </span>
                        </div>
                        
                        {/* Contador regressivo */}
                        <div className="mt-2">
                          <AppointmentCountdown 
                            date={appointment.date} 
                            time={appointment.time}
                            size="sm"
                            showLabel={true}
                          />
                        </div>
                        
                        <p className="text-sm mt-2 font-medium">
                          {appointment.provider_name || 'Lava-Jato'}
                        </p>
                        {appointment.provider_address && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {appointment.provider_address}
                          </p>
                        )}
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </Card>
                );
              })}
              
              {activeAppointments.length > 3 && (
                <Button 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => navigate('/client/appointments')}
                >
                  Ver todos ({activeAppointments.length})
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Histórico Recente */}
        {recentCompleted.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <History className="w-5 h-5 text-muted-foreground" />
                Histórico Recente
              </h2>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/client/appointments')}
              >
                Ver tudo
              </Button>
            </div>

            <div className="space-y-3">
              {recentCompleted.map(appointment => {
                const statusConfig = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.COMPLETED;
                const serviceLabel = SERVICE_TYPE_LABELS[appointment.service_type] || appointment.service_type || 'Lavagem';
                const isDisputed = disputedAppointments.includes(appointment.id);
                
                return (
                  <Card key={appointment.id} className="p-4 bg-slate-50">
                    <div className="space-y-3">
                      {/* Linha 1: Nome do prestador e status */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="bg-white p-2 rounded-full">
                            <Car className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{appointment.provider_name || 'Lava-jato'}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(appointment.date)} às {appointment.time}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </div>
                      {/* Badge sutil de status de contestação (não substitui o status operacional) */}
                      {(() => {
                        const fs = appointment.financial_status;
                        const dr = appointment.dispute_resolution;
                        let dBadge = null;
                        if (fs === 'em_analise') dBadge = { label: 'Contestação em análise', cls: 'bg-orange-50 text-orange-700 border-orange-200' };
                        else if (fs === 'revertido' || dr === 'client_won') dBadge = { label: 'Reembolsado', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
                        else if (dr === 'provider_won') dBadge = { label: 'Contestação recusada', cls: 'bg-slate-50 text-slate-600 border-slate-200' };
                        return dBadge ? (
                          <Badge variant="outline" className={`text-[11px] ${dBadge.cls}`} data-testid={`client-dispute-badge-${appointment.id}`}>
                            {dBadge.label}
                          </Badge>
                        ) : null;
                      })()}
                      
                      {/* Linha 2: Tipo de serviço */}
                      <div className="flex items-center text-sm pl-11">
                        <span className="text-muted-foreground flex items-center gap-1">
                          {appointment.service_type === 'complete' || appointment.service_type === 'lavagem_completa' ? (
                            <Sparkles className="w-3 h-3" />
                          ) : (
                            <Droplets className="w-3 h-3" />
                          )}
                          {serviceLabel}
                        </span>
                      </div>
                      
                      {/* Linha 3: Ações */}
                      <div className="flex gap-2 pl-11">
                        {canDispute(appointment) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenDispute(appointment)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Abrir Contestação
                          </Button>
                        )}
                        
                        {isDisputed && (
                          <Badge variant="outline" className="text-orange-600 border-orange-300">
                            Contestação em análise
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
        </>)}
      </div>

      {/* Modal de Avaliação */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        appointmentData={selectedReview}
        onReviewSubmitted={handleReviewSubmitted}
      />
      
      {/* Modal de Contestação */}
      <Dialog open={disputeModalOpen} onOpenChange={setDisputeModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Abrir Contestação
            </DialogTitle>
            <DialogDescription>
              Informe o motivo e descreva o problema com o serviço realizado.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo da contestação</Label>
              <Select value={disputeMotivo} onValueChange={setDisputeMotivo}>
                <SelectTrigger id="motivo">
                  <SelectValue placeholder="Selecione o motivo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="servico_incompleto">Serviço incompleto</SelectItem>
                  <SelectItem value="qualidade_insatisfatoria">Qualidade insatisfatória</SelectItem>
                  <SelectItem value="danos_veiculo">Danos ao veículo</SelectItem>
                  <SelectItem value="nao_compareceu">Prestador não compareceu</SelectItem>
                  <SelectItem value="cobranca_indevida">Cobrança indevida</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição do problema</Label>
              <Textarea
                id="descricao"
                placeholder="Descreva detalhadamente o que aconteceu (mínimo 20 caracteres)..."
                value={disputeDescricao}
                onChange={(e) => setDisputeDescricao(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {disputeDescricao.length}/20 caracteres mínimos
              </p>
            </div>
            
            {disputeAppointment && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p className="font-medium">{disputeAppointment.provider_name}</p>
                <p className="text-muted-foreground">
                  {formatDate(disputeAppointment.date)} às {disputeAppointment.time}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmitDispute} 
              disabled={disputeLoading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {disputeLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              Enviar Contestação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Suporte */}
      <SupportModal 
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        onTicketCreated={() => toast.success('Ticket criado!')}
      />
    </div>
  );
}
